import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as workspaceApi from '../../apis/workspace';

interface WorkspaceState {
    plans: any[];
    flashcardSets: any[];
    exercises: any[];
    loading: boolean;
    error: string | null;
}

const initialState: WorkspaceState = {
    plans: [],
    flashcardSets: [],
    exercises: [],
    loading: false,
    error: null,
};

export const fetchLessonResources = createAsyncThunk(
    'workspace/fetchResources',
    async (lessonId: string, { rejectWithValue }) => {
        try {
            const [plansRes, cardsRes, exercisesRes] = await Promise.all([
                workspaceApi.getLessonPlans(lessonId),
                workspaceApi.getFlashcardSets(lessonId),
                require('../../apis/exercise').getLessonExercises(lessonId)
            ]);
            return {
                plans: plansRes.data,
                flashcardSets: cardsRes.data,
                exercises: exercisesRes.data
            };
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.detail || 'Failed to fetch resources');
        }
    }
);

export const saveLessonPlan = createAsyncThunk(
    'workspace/savePlan',
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await workspaceApi.createLessonPlan(data);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.detail || 'Failed to save lesson plan');
        }
    }
);

export const saveFlashcardSet = createAsyncThunk(
    'workspace/saveFlashcards',
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await workspaceApi.createFlashcardSet(data);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.detail || 'Failed to save flashcards');
        }
    }
);

const workspaceSlice = createSlice({
    name: 'workspace',
    initialState,
    reducers: {
        resetWorkspace: (state) => {
            state.plans = [];
            state.flashcardSets = [];
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLessonResources.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchLessonResources.fulfilled, (state, action) => {
                state.loading = false;
                state.plans = action.payload.plans;
                state.flashcardSets = action.payload.flashcardSets;
                state.exercises = action.payload.exercises;
            })
            .addCase(fetchLessonResources.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(saveLessonPlan.fulfilled, (state, action) => {
                state.plans.push(action.payload);
            })
            .addCase(saveFlashcardSet.fulfilled, (state, action) => {
                state.flashcardSets.push(action.payload);
            });
    }
});

export const { resetWorkspace } = workspaceSlice.actions;
export default workspaceSlice.reducer;
